import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	CheckOutlined,
	CloseOutlined,
	EditOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import Input from 'antd/lib/input';
import InputNumber from 'antd/es/input-number';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Card from 'antd/lib/card';
import { Col } from 'antd/lib/grid';
import Table from 'antd/lib/table';
import Typography from 'antd/lib/typography/Typography';
import { formatCurrency } from '@shared/utils/currency';
import {
	EstimateStatus,
	VesselCostType,
} from '@shared/utils/constants';
import { formatNumber } from '@shared/utils/formatNumber';
import { calculateTotal } from '@shared/utils/math';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { EstimateCostProps } from '@api/models/estimate-cost';
import type { GetEstimateCostsResponse } from '@api/features/estimates/utils/getEstimateCosts';
import showErrorNotification from '@client/utils/showErrorNotification';
import {
	updateEstimateCostToEstimate,
	deleteEstimateCost,
	addEstimateCostToEstimate,
} from '@client/lib/api';
import CurrencySelect from '@client/components/CurrencySelect/CurrencySelect';
import AddButton from '@client/components/AddButton';
import { OnUpdateEstimate } from '../details/context/hooks/useEstimateHandlers';
import styles from '../details/styles/EstimateCost.module.css';

type EstimateCostsProps = {
	estimate: GetEstimateDetailsResponse;
	estimateCosts: GetEstimateCostsResponse | undefined;
	onUpdateEstimate: OnUpdateEstimate;
	refreshEstimateCosts: () => Promise<any>;
	setEditEstimateCostMode: (editing: boolean) => void;
};

const EstimateCosts = (props: EstimateCostsProps) => {
	const {
		estimate,
		estimateCosts,
		onUpdateEstimate,
		refreshEstimateCosts,
		setEditEstimateCostMode,
	} = props;

	const [editingRowKey, setEditingRowKey] = useState<number | string | null>(null);
	const [formValues, setFormValues] = useState<Partial<EstimateCostProps>>({});

	const calculateTotalInBaseCurrency = useCallback(
		(costs: EstimateCostProps[] | undefined): number => {
			if (!costs || costs.length === 0) {
				return 0;
			}

			return calculateTotal(costs, (c) => c.amount);
		}, [],
	);

	const estimatedDuration = estimate?.result?.estimatedDuration ?? 0;

	const handleChange = useCallback(
		(field: keyof EstimateCostProps, value: any) => {
			setFormValues((prev) => {
				let units = prev.units ?? 0;
				const updated: Partial<EstimateCostProps> = { ...prev, [field]: value };

				if (field === 'unitType') {
					if (value === VesselCostType.Lumpsum) {
						units = 1;
					} else if (value === VesselCostType.PerDay) {
						units = estimatedDuration;
					}

					updated.units = units;
				}

				const unitPrice = updated.unitPrice ?? 0;
				const localAmount = (updated.units ?? 0) * unitPrice;
				const rate = updated.exchangeRate ?? 1;

				updated.localAmount = localAmount;
				updated.amount = localAmount / rate;

				return updated;
			});
		},
		[estimatedDuration],
	);

	const handleEditSave = useCallback(
		async (record: EstimateCostProps) => {
			const updated = { ...record, ...formValues };

			const values: EstimateCostProps = {
				amount: updated.amount,
				currency: updated.currency,
				description: updated.description,
				estimateId: estimate?.id,
				exchangeRate: updated.exchangeRate ?? 1,
				localAmount: updated.localAmount,
				unitPrice: updated.unitPrice,
				units: updated.units,
				unitType: updated.unitType,
			} as EstimateCostProps;

			let addedCost = 0;

			if (String(record.id) === 'new') {
				await addEstimateCostToEstimate(values);
				addedCost = updated.amount ?? 0;
			} else {
				await updateEstimateCostToEstimate(updated.id, estimate.id, values);
			}

			const updatedCosts =
				(estimateCosts ?? []).map((c) => (c.id === updated.id ? { ...c, ...values } : c));

			const total = calculateTotalInBaseCurrency(updatedCosts) + addedCost;

			onUpdateEstimate({ miscCostTotal: total });

			await refreshEstimateCosts();
			setEditingRowKey(null);
			setFormValues({});
			setEditEstimateCostMode(false);
		},
		[estimate.id, estimateCosts, formValues,
			onUpdateEstimate, refreshEstimateCosts,
			setEditEstimateCostMode, calculateTotalInBaseCurrency],
	);

	const handleDelete = useCallback(
		async (id: number) => {
			try {
				await deleteEstimateCost(estimate.id, id);

				const remaining = (estimateCosts ?? []).filter((c) => c.id !== id);
				const total = calculateTotalInBaseCurrency(remaining);

				onUpdateEstimate({ miscCostTotal: total });
				await refreshEstimateCosts();
			} catch {
				showErrorNotification('Cannot delete estimate cost');
			}
		},
		[
			estimate.id,
			estimateCosts,
			onUpdateEstimate,
			refreshEstimateCosts,
			calculateTotalInBaseCurrency,
		],
	);

	const isEditing = useCallback((record: EstimateCostProps) => record.id === editingRowKey,
		[editingRowKey]);
	const isEditMode = editingRowKey !== null;
	const currencyColWidth = estimate.status === EstimateStatus.Fixed || !isEditMode ? 200 : 500;

	const columns = useMemo(() => {
		const base = [
			{
				title: 'Description',
				dataIndex: 'description',
				render: (_: any, record: EstimateCostProps) => (isEditing(record) ? (
					<Input
						value={formValues.description ?? record.description}
						onChange={(e) => handleChange('description', e.target.value)}
					/>
				) : (
					record.description
				)),
				width: 200,
			},
			{
				title: 'Type',
				dataIndex: 'unitType',
				render: (_: any, record: EstimateCostProps) => (isEditing(record) ? (
					<Select
						value={formValues.unitType ?? record.unitType}
						onChange={(v) => handleChange('unitType', v)}
						options={[
							{ label: '/day', value: VesselCostType.PerDay },
							{ label: 'Lumpsum', value: VesselCostType.Lumpsum },
						]}
					/>
				) : (
					record.unitType
				)),
				width: 250,
			},
			{
				title: 'Units',
				dataIndex: 'units',
				render: (_: any, record: EstimateCostProps) => (isEditing(record) ? (
					<InputNumber
						readOnly={formValues.unitType === VesselCostType.PerDay}
						value={formValues.units?.toFixed(2) ?? record.units.toFixed(2)}
						onChange={(v) => handleChange('units', v)}
						width={160}
						disabled={formValues.unitType === VesselCostType.PerDay}
					/>
				) : (
					record.units.toFixed(2)
				)),
				width: 160,
			},
			{
				title: 'Unit Price',
				dataIndex: 'unitPrice',
				render: (_: any, record: EstimateCostProps) => (isEditing(record) ? (
					<InputNumber
						value={formValues.unitPrice?.toFixed(2) ?? record.unitPrice?.toFixed(2)}
						onChange={(v) => handleChange('unitPrice', v)}
					/>
				) : (
					formatNumber(record.unitPrice ?? 0, { separateThousands: true }, 2)
				)),
				width: 200,
			},
			{
				title: 'Currency',
				dataIndex: 'currency',
				render: (_: any, record: EstimateCostProps) => (isEditing(record) ? (
					<CurrencySelect
						baseCurrency={estimate.currency}
						hideLabels
						value={{
							currency: formValues.currency ?? record.currency,
							exchangeRate:
								Number(formValues.exchangeRate?.toFixed(2)) ??
								Number(record.exchangeRate.toFixed(2)),
						}}
						onChange={(c, e) => {
							handleChange('currency', c);
							handleChange('exchangeRate', e);
						}}
					/>
				) : (
					record.currency
				)),
				width: currencyColWidth,
				height: 400,
			},
			{
				title: 'Local',
				render: (_: any, record: EstimateCostProps) => (isEditing(record) ?
					formatCurrency(
						formValues.localAmount ?? 0,
						formValues.currency ?? estimate.currency,
					) :
					formatCurrency(record.localAmount ?? 0, record.currency ?? estimate.currency)),
				width: 200,
			},
			{
				title: `Amount ${estimate.currency}`,
				dataIndex: 'amount',
				render: (_: any, record: EstimateCostProps) => (isEditing(record) ?
					formatCurrency(formValues.amount ?? 0, estimate.currency) :
					formatCurrency(record.amount ?? 0, estimate.currency)),
				width: 200,
			},
		];

		if (estimate.status !== EstimateStatus.Fixed) {
			base.push({
				title: 'Actions',
				width: 400,
				render: (_: any, record: EstimateCostProps) => (isEditing(record) ? (
					<>
						<Button
							type="link"
							onClick={() => handleEditSave(record)}
							icon={(<CheckOutlined className={styles.buttonIcon} />)}
						/>
						<Button
							type="link"
							onClick={() => {
								setEditEstimateCostMode(false);
								setEditingRowKey(null);
							}}
							icon={(<CloseOutlined className={styles.buttonIcon} />)}
							danger
						/>
					</>
				) : (
					<>
						<Button
							type="link"
							onClick={() => {
								setEditingRowKey(record.id);
								setEditEstimateCostMode(true);
								setFormValues(record);
							}}
							icon={(<EditOutlined className={styles.buttonIcon} />)}
						/>
						<Button
							type="link"
							danger
							onClick={() => handleDelete(record.id!)}
							icon={(<DeleteOutlined className={styles.buttonIcon} />)}
						/>
					</>
				)),
			} as any);
		}

		return base;
	}, [currencyColWidth, estimate, isEditing,
		formValues, handleChange, handleEditSave,
		setEditEstimateCostMode, handleDelete]);

	const getDataSource = (): EstimateCostProps[] => {
		if (editingRowKey === 'new') {
			return [
				...(estimateCosts ?? []),
				{
					...formValues,
					id: 'new',
					estimateId: estimate.id,
					amount: formValues.amount ?? 0,
					currency: formValues.currency ?? estimate.currency,
					description: formValues.description ?? '',
					unitType: formValues.unitType ?? VesselCostType.Lumpsum,
					units: formValues.units ?? 1,
					unitPrice: formValues.unitPrice ?? 0,
				} as unknown as EstimateCostProps,
			];
		}

		return estimateCosts ?? [];
	};

	const handleAddNewCost = useCallback(() => {
		setEditingRowKey('new');
		setEditEstimateCostMode(true);
		setFormValues({
			unitType: VesselCostType.Lumpsum,
			units: 1,
			unitPrice: 0,
			currency: estimate.currency,
		});
	}, [estimate.currency, setEditEstimateCostMode]);

	return (
		<Card size="small">
			<Col>
				<Space className={styles.mainButtons}>
					<Typography className={styles.addButtonTitle}>Add new estimate cost</Typography>
					{estimate.status !== EstimateStatus.Fixed && (
						<AddButton
							onClick={handleAddNewCost}
							size="small"
							disabled={isEditMode}
						/>
					)}
				</Space>
				<Table
					dataSource={getDataSource().sort((a, b) => a.id - b.id)}
					rowKey={(record) => record.id.toString()} // Ensures key is string
					pagination={false}
					columns={columns}
					summary={(data: readonly EstimateCostProps[]) => {
						const rows = [...data];

						const totalAmountBase = rows.reduce((s, r) => s + (r.amount ?? 0), 0);

						const sameCurrency =
							rows.length > 0 && rows.every((r) => r.currency === rows[0]!.currency);
						const localCurrency = sameCurrency ? rows[0]!.currency : undefined;
						const totalLocal = sameCurrency ?
							rows.reduce((s, r) => s + (r.localAmount ?? 0), 0) :
							0;

						const colCount = columns.length;
						const hasActions = columns[colCount - 1]?.title.toLowerCase() === 'actions';

						const localIdx = hasActions ? colCount - 3 : colCount - 2;
						const amountIdx = hasActions ? colCount - 2 : colCount - 1;
						const labelColSpan = localIdx;

						return (
							<Table.Summary fixed>
								<Table.Summary.Row>
									<Table.Summary.Cell index={0} colSpan={labelColSpan}>
										<strong>Total</strong>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={localIdx}>
										{sameCurrency ? (
											<strong>{formatCurrency(totalLocal, localCurrency!)}</strong>
										) : (
											<em style={{ opacity: 0.7 }}>—</em>
										)}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={amountIdx}>
										<strong>{formatCurrency(totalAmountBase, estimate.currency)}</strong>
									</Table.Summary.Cell>
									{hasActions && (<Table.Summary.Cell index={colCount - 1} />)}
								</Table.Summary.Row>
							</Table.Summary>
						);
					}}
				/>
			</Col>
		</Card>
	);
};

export default EstimateCosts;
